import { useId, useSyncExternalStore, type CSSProperties, type ReactNode, type TableHTMLAttributes } from 'react'
import { getDensity, setDensity, subscribeDensity, type Density } from '../../theme/theme'
import { cx } from './cx'
import { Icon } from './Icon'
import { IconButton } from './Button'
import { ButtonGroup } from './ButtonGroup'
import { EmptyState } from './Feedback'
import { Select } from './Fields'

/** Densita' scelta dall'utente (0, -1, -2, -3), condivisa da tabelle, liste e campi. */
export function useDensity(): [Density, (d: Density) => void] {
  const d = useSyncExternalStore(subscribeDensity, getDensity, () => 0 as Density)
  return [d, setDensity]
}

export interface DataTableProps extends Omit<TableHTMLAttributes<HTMLTableElement>, 'aria-label'> {
  /**
   * contained (default) = bordo outline-variant e raggio 4; plain = senza bordo, da usare dentro una Card.
   */
  variant?: 'contained' | 'plain' | undefined
  /** Piede della tabella (di solito <Pagination>), dentro il contenitore. */
  footer?: ReactNode | undefined
  stickyHeader?: boolean | undefined
  /** Altezza massima del contenitore con stickyHeader (default calc(100dvh - 220px)). */
  maxHeight?: string | undefined
  /** Didascalia per screen reader. */
  caption?: string | undefined
  'aria-label'?: string | undefined
  children: ReactNode
}

/** Tabella dati: i figli (thead/tbody) restano invariati rispetto a `<table class="table">`. */
export function DataTable({
  variant = 'contained',
  footer,
  stickyHeader,
  maxHeight,
  caption,
  'aria-label': ariaLabel,
  className,
  children,
  ...rest
}: DataTableProps) {
  const style = stickyHeader && maxHeight ? ({ '--max': maxHeight } as CSSProperties) : undefined
  return (
    <div className={cx('md-table-container', `md-table-container--${variant}`)}>
      <div
        className={cx('md-table-wrap', stickyHeader && 'md-table-wrap--sticky')}
        style={style}
        {...(ariaLabel ? { role: 'region', 'aria-label': ariaLabel, tabIndex: 0 } : {})}
      >
        <table {...rest} className={cx('md-table', className)}>
          {caption ? <caption className="md-sr-only">{caption}</caption> : null}
          {children}
        </table>
      </div>
      {footer ? <div className="md-table-footer">{footer}</div> : null}
    </div>
  )
}

export interface DataTableEmptyRowProps {
  colSpan: number
  icon?: string | undefined
  title: string
  description?: string | undefined
}

export function DataTableEmptyRow({ colSpan, icon = 'inbox', title, description }: DataTableEmptyRowProps) {
  return (
    <tr className="md-table__empty">
      <td colSpan={colSpan}>
        <EmptyState icon={icon} title={title} description={description} compact />
      </td>
    </tr>
  )
}

/** Selettore della densita' (0, -1, -2, -3): scrive ecrm_density e applica data-density a <html>. */
export function DensityToggle() {
  const [d, set] = useDensity()
  return (
    <ButtonGroup
      aria-label="Densità dell'interfaccia"
      value={String(d)}
      onChange={(v) => set(Number(v) as Density)}
      options={[
        { value: '0', label: 'Comoda' },
        { value: '-1', label: 'Media' },
        { value: '-2', label: 'Compatta' },
        { value: '-3', label: 'Minima' },
      ]}
    />
  )
}

export interface PaginationProps {
  /** Pagina corrente, a partire da 1. */
  page: number
  pageSize: number
  total: number
  onChange: (page: number) => void
  /** Con `pageSizeOptions` e `onPageSizeChange` compare "Righe per pagina". */
  pageSizeOptions?: readonly number[] | undefined
  onPageSizeChange?: ((size: number) => void) | undefined
}

export function Pagination({ page, pageSize, total, onChange, pageSizeOptions, onPageSizeChange }: PaginationProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(total, page * pageSize)
  const sizeId = useId()
  return (
    <nav className="md-pagination" aria-label="Paginazione">
      {pageSizeOptions && onPageSizeChange ? (
        <span className="md-pagination__size">
          <span id={sizeId}>Righe per pagina</span>
          <Select
            label="Righe per pagina"
            labelHidden
            className="md-pagination__select"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
          >
            {(pageSizeOptions.includes(pageSize) ? pageSizeOptions : [...pageSizeOptions, pageSize].sort((x, y) => x - y)).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </span>
      ) : null}
      <span className="md-pagination__range md-num" aria-live="polite">
        {from}-{to} di {total}
      </span>
      <IconButton icon="first_page" size="sm" aria-label="Prima pagina" disabled={page <= 1} onClick={() => onChange(1)} />
      <IconButton icon="chevron_left" size="sm" aria-label="Pagina precedente" disabled={page <= 1} onClick={() => onChange(page - 1)} />
      <span className="md-pagination__page md-num">
        {page} / {pages}
      </span>
      <IconButton icon="chevron_right" size="sm" aria-label="Pagina successiva" disabled={page >= pages} onClick={() => onChange(page + 1)} />
      <IconButton icon="last_page" size="sm" aria-label="Ultima pagina" disabled={page >= pages} onClick={() => onChange(pages)} />
    </nav>
  )
}

export interface SortHeaderProps {
  label: string
  sortKey: string
  activeKey: string | null | undefined
  direction: 'asc' | 'desc'
  onSort: (key: string) => void
  /** Allinea a destra (colonne numeriche). */
  numeric?: boolean | undefined
  className?: string | undefined
}

/** Intestazione ordinabile: unifica SortableTh e SortKeyTh. */
export function SortHeader({ label, sortKey, activeKey, direction, onSort, numeric, className }: SortHeaderProps) {
  const active = activeKey === sortKey
  return (
    <th
      scope="col"
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cx('md-table__sortable', numeric && 'md-num', className)}
    >
      <button type="button" className="md-sort md-state" onClick={() => onSort(sortKey)}>
        <span>{label}</span>
        <Icon name={active ? (direction === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'} size={18} className={cx('md-sort__icon', !active && 'md-sort__icon--idle')} />
      </button>
    </th>
  )
}
